import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import {
  Plane,
  FileText,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BarChart3,
  Calculator,
  Scale,
  LayoutDashboard,
  Terminal,
  Grid3X3,
  Armchair,
  Bug,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RoleSwitcher } from '@/components/ui/role-switcher';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { AmadeusStatusIndicator } from './AmadeusStatusIndicator';
import { AsyncStatusIndicator } from './AsyncStatusIndicator';

// Main navigation items
const mainNavItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/lawyer/dashboard',
    description: 'Übersicht und anstehende Aufgaben',
  },
  {
    id: 'cases',
    label: 'Akten',
    icon: FileText,
    path: '/lawyer/cases',
    description: 'Akten und Entschädigungsfälle',
  },
  {
    id: 'kontakte',
    label: 'Kontakte',
    icon: Users,
    path: '/lawyer/kontakte',
    description: 'Mandanten, Beklagte und Kontakte',
  },
];

// Proof of concept / tools section
const toolsNavItems = [
  {
    id: 'bookings',
    label: 'Buchungen',
    icon: Plane,
    path: '/lawyer/bookings',
    description: 'Lufthansa-Buchungen suchen und überwachen',
  },
  {
    id: 'availability',
    label: 'Verfügbarkeit',
    icon: Grid3X3,
    path: '/lawyer/availability',
    description: 'Flugverfügbarkeit und Buchungsklassen',
  },
  {
    id: 'seatmap',
    label: 'Seat Map',
    icon: Armchair,
    path: '/lawyer/seatmap',
    description: 'Sitzplatzkonfiguration und Verfügbarkeit',
  },
  {
    id: 'pricing',
    label: 'Tarifpreise',
    icon: Calculator,
    path: '/lawyer/pricing',
    description: 'Historische Tarifpreise',
  },
  {
    id: 'flight-status',
    label: 'Flugstatus',
    icon: BarChart3,
    path: '/lawyer/flight-status',
    description: 'Flugstatus und Verspätungen prüfen',
  },
  {
    id: 'console',
    label: 'Console',
    icon: Terminal,
    path: '/lawyer/console',
    description: 'Amadeus Cryptic Befehle ausführen',
  },
  {
    id: 'debug',
    label: 'Debug',
    icon: Bug,
    path: '/lawyer/debug',
    description: 'Amadeus Session Monitoring',
    staffOnly: true,
  },
];

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { organizationName, logoUrl } = useTheme();

  // Load collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('lawyer-sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('lawyer-sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const handleNavigation = (path) => {
    navigate({ to: path });
  };

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-sidebar-border transition-all duration-200 ease-in-out hidden md:flex flex-col',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo/Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={organizationName} className="h-9 w-auto" />
              ) : (
                <>
                  <Scale className="h-5 w-5 text-brand-from" />
                  <span className="text-lg font-semibold bg-gradient-to-r from-brand-from to-brand-to bg-clip-text text-transparent">
                    {organizationName}
                  </span>
                </>
              )}
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn(
              'p-2 rounded-lg hover:bg-sidebar-border transition-colors',
              isCollapsed && 'mx-auto'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-sidebar-text" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-sidebar-text" />
            )}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-4 px-2 overflow-y-auto">
          {/* Main Section */}
          <div className="space-y-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              const button = (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150',
                    active
                      ? 'bg-sidebar-active text-action-text shadow-lg shadow-sidebar-active/25'
                      : 'text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-border'
                  )}
                >
                  <Icon className={cn('h-5 w-5 shrink-0', active && 'text-action-text')} />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </button>
              );

              if (isCollapsed) {
                return (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                      <p className="font-medium">{item.label}</p>
                      <p className="text-xs text-slate-400">{item.description}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return button;
            })}
          </div>

          {/* Tools Section */}
          <div className="mt-6 pt-4 border-t border-sidebar-border">
            {!isCollapsed && (
              <p className="text-xs text-sidebar-text/70 px-3 mb-2 uppercase tracking-wider">Werkzeuge</p>
            )}
            <div className="space-y-1">
              {toolsNavItems
                .filter((item) => !item.staffOnly || user?.is_staff || user?.is_superuser)
                .map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                const button = (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150',
                      active
                        ? 'bg-sidebar-active text-action-text shadow-lg shadow-sidebar-active/25'
                        : 'text-sidebar-text hover:text-sidebar-text-hover hover:bg-sidebar-border'
                    )}
                  >
                    <Icon className={cn('h-5 w-5 shrink-0', active && 'text-action-text')} />
                    {!isCollapsed && (
                      <span className="text-sm font-medium">{item.label}</span>
                    )}
                  </button>
                );

                if (isCollapsed) {
                  return (
                    <Tooltip key={item.id}>
                      <TooltipTrigger asChild>{button}</TooltipTrigger>
                      <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                        <p className="font-medium">{item.label}</p>
                        <p className="text-xs text-slate-400">{item.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return button;
              })}
            </div>
          </div>
        </nav>

        {/* Role Switcher - only show if user has both lawyer AND client roles */}
        {user?.is_lawyer && user?.is_client && (
          <div className="border-t border-sidebar-border p-3">
            {!isCollapsed && (
              <p className="text-xs text-sidebar-text/70 mb-2 px-1">Ansicht wechseln</p>
            )}
            <RoleSwitcher
              variant="vertical"
              size="sm"
              showLabels={!isCollapsed}
              className={cn(isCollapsed && 'items-center')}
            />
          </div>
        )}

        {/* Status Indicators */}
        <div className="border-t border-sidebar-border">
          <AmadeusStatusIndicator isCollapsed={isCollapsed} />
          <AsyncStatusIndicator isCollapsed={isCollapsed} />
        </div>

        {/* User Section */}
        <div className="border-t border-sidebar-border p-4">
          {!isCollapsed && user && (
            <div className="mb-3 px-2">
              <p className="text-sm font-medium text-sidebar-text-hover truncate">{user.email}</p>
              <p className="text-xs text-sidebar-text/70">Anwalt</p>
            </div>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleLogout}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-text hover:text-red-500 hover:bg-red-50 transition-colors',
                  isCollapsed && 'justify-center'
                )}
              >
                <LogOut className="h-5 w-5 shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">Abmelden</span>}
              </button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700">
                Abmelden
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
}
